import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchField from "../SearchField";
import { useEffect, useState } from "react";
import { EingangsrechnungInfo, RechnungenListe } from "../../api";
import {
  useGetRechnung,
  useKorrekturRechnungZuweisen,
  useSucheZuweisbareEingangsrechnungen,
  useUpdateRechnung,
} from "../../client/hooks";
import { useMutation, useQueryClient } from "react-query";
import { Field, useField } from "formik";
import * as yup from "yup";
import { TextField } from "formik-mui";
import { useNavigate } from "react-router";
import { useT } from "../../i18n";
import RechnungStatusChip from "./RechnungStatusChip";
import { formatAsMoney, formatDate } from "../../formatterHelpers";
import { formValuesToRechnungData } from "../../pages/rechnungen/RechnungsFormular";

export default function EingangsrechnungenZuweisungsListe({
  korrekturRechnungId,
  zugewieseneRechnungId,
  belegTyp,
  lieferantId,
  bruttoBetrag,
  rechnungsBetrag,
  rechnungsBetragAbweichend,
  values,
}: {
  korrekturRechnungId: string | undefined;
  zugewieseneRechnungId: string | undefined;
  belegTyp: string;
  lieferantId: string;
  bruttoBetrag: number;
  rechnungsBetrag: number | undefined;
  rechnungsBetragAbweichend: boolean;
  values: any;
}) {
  const navigate = useNavigate();
  const { t, capitalize } = useT("eingangsrechnung");

  const korrekturRechnungZuweisen = useKorrekturRechnungZuweisen();
  const updateRechnung = useUpdateRechnung();

  const [stornoDialogOpen, setStornoDialogOpen] = useState(false);

  const [zuweisbareRechnungen, setZuweisbareRechnungen] =
    useState<RechnungenListe>();

  const [search, setSearch] = useState<string>("");

  const [checkedRechnungId, setCheckedRechnungId] = useState<
    string | undefined
  >(zugewieseneRechnungId);
  const [ausgewaehlteRechnungId, setAusgewaehlteRechnungId] = useState<
    string | null
  >(null);

  const [loading, setLoading] = useState(false);

  const [isZugewieseneRechnungBezahlt, setisZugewieseneRechnungBezahlt] =
    useState<boolean>(false);
  const [isRechnungZugewiesenUndStorno, setIsRechnungZugewiesenUndStorno] =
    useState(false);

  const searchZuweisbareEingangsrechnungen =
    useSucheZuweisbareEingangsrechnungen();
  const queryClient = useQueryClient();

  const bekommeRechnung = useGetRechnung();
  const [startDateField] = useField(`startDateRechnung`);
  const [endDateField] = useField(`endDateRechnung`);
  const fetchZweisbareRechnungen = async () => {
    const rechnungen = await searchZuweisbareEingangsrechnungen(
      korrekturRechnungId ?? "",
      {
        korrekturBelegTyp: belegTyp,
        search: search,
        lieferantId: lieferantId,
        bruttoBetrag: formatNumber(bruttoBetrag) ?? undefined,
        startDate: startDateField.value,
        endDate: endDateField.value,
      }
    );

    let neueItems = [...rechnungen.items];

    if (
      checkedRechnungId &&
      !neueItems.some((rechnung) => rechnung.id == checkedRechnungId)
    ) {
      const checkedRechnung = await bekommeRechnung(checkedRechnungId);
      if (checkedRechnung) {
        neueItems = [checkedRechnung, ...neueItems];
      }
    }
    setZuweisbareRechnungen({
      ...rechnungen,
      items: neueItems,
    });
  };

  const fetchZugewieseneRechnung = async () => {
    if (zugewieseneRechnungId) {
      const zugewieseneRechnung = await bekommeRechnung(zugewieseneRechnungId);
      setisZugewieseneRechnungBezahlt(
        zugewieseneRechnung.bezahltStatus === "BEZAHLT"
      );
    }
  };

  useEffect(() => {
    fetchZugewieseneRechnung();
  }, [zugewieseneRechnungId]);

  useEffect(() => {
    fetchZweisbareRechnungen();
  }, [
    korrekturRechnungId,
    belegTyp,
    lieferantId,
    bruttoBetrag,
    search,
    startDateField.value,
    endDateField.value,
  ]);

  useEffect(() => {
    setIsRechnungZugewiesenUndStorno(
      !!checkedRechnungId && belegTyp.toUpperCase() === "STORNO"
    );
  }, [checkedRechnungId]);

  const weiseRechnungZu = useMutation(
    (params: {
      korrekturRechnungId: string;
      belegTyp: string;
      rechnungId: string;
    }) => {
      return korrekturRechnungZuweisen(params.korrekturRechnungId, {
        korrekturBelegTyp: params.belegTyp,
        rechnungId: params.rechnungId,
      });
    },
    {
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: () => {
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
      },
    }
  );

  const handleToggle = (id: any) => {
    const newCheckedId = checkedRechnungId === id ? undefined : id;
    setCheckedRechnungId(newCheckedId);

    if (korrekturRechnungId) {
      try {
        updateRechnung(
          korrekturRechnungId,
          formValuesToRechnungData(values)
        ).then(() => {
          weiseRechnungZu.mutate({
            korrekturRechnungId: korrekturRechnungId,
            belegTyp: belegTyp,
            rechnungId: newCheckedId,
          });
          queryClient.refetchQueries(["rechnungen"]).then(() => {
            console.log("refetch");
          });
        });
      } catch (error) {
        console.error("Failed to update invoice:", error);
      }
    }
  };

  let schema = yup.object().shape({
    startDateRechnung: yup.string(),
    endDateRechnung: yup.string(),
  });

  function equalsIgnoreCase(str1: string, str2: string) {
    return str1.toLowerCase() === str2.toLowerCase();
  }

  const formatNumber = (value: number) => {
    if (!value) return null;
    const formattedValue = value.toString().replace(".", "").replace(",", ".");
    const parsedValue = parseFloat(formattedValue);
    return isNaN(parsedValue) ? null : parsedValue;
  };

  const bruttoBetragFormatted = formatNumber(bruttoBetrag);
  const rechnungsBetragFormatted = rechnungsBetrag
    ? formatNumber(rechnungsBetrag)
    : null;

  const disabledRechnungsZuweisung =
    (!lieferantId ||
      (rechnungsBetragAbweichend
        ? rechnungsBetragFormatted === null
        : bruttoBetragFormatted === null)) &&
    !checkedRechnungId;
  const rechnungZugewiesen =
    isZugewieseneRechnungBezahlt || isRechnungZugewiesenUndStorno;

  const visibleRechnungsZuweisung =
    equalsIgnoreCase(values.belegtyp, "Gutschrift") ||
    equalsIgnoreCase(values.belegtyp, "Storno");

  return (
    <>
      <Accordion
        disabled={disabledRechnungsZuweisung}
        hidden={!visibleRechnungsZuweisung}
        defaultExpanded={true}
      >
        <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
          {!rechnungZugewiesen
            ? capitalize(t("rechnung-assign"))
            : capitalize(t("rechnung-assign-past"))}
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer
            component={Paper}
            sx={{
              opacity: disabledRechnungsZuweisung ? 0.5 : 1,
              pointerEvents: disabledRechnungsZuweisung ? "none" : "auto",
              background: disabledRechnungsZuweisung ? "#DDDDDD" : "#FFFFFF",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              {!rechnungZugewiesen && (
                <>
                  <SearchField
                    value={search}
                    onChange={(value) => {
                      setSearch(value);
                    }}
                  ></SearchField>
                  <Field
                    sx={{ ml: 2 }}
                    component={TextField}
                    name={`startDateRechnung`}
                    label={"von"}
                    type="date"
                    size={"small"}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: endDateField.value,
                    }}
                  />
                  <Field
                    sx={{ ml: 2 }}
                    component={TextField}
                    name={`endDateRechnung`}
                    label={"bis"}
                    type="date"
                    size={"small"}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: startDateField.value,
                    }}
                  />
                </>
              )}
            </Stack>
            <Table size={"small"}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    {!rechnungZugewiesen ? capitalize(t("assign")) : ""}
                  </TableCell>
                  <TableCell>Datum</TableCell>
                  <TableCell>Rechnungsnr</TableCell>
                  <TableCell sx={{ textAlign: "right" }}>Brutto</TableCell>
                  <TableCell sx={{ textAlign: "right" }}>Netto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {zuweisbareRechnungen ? (
                  zuweisbareRechnungen.items.map(
                    (zuweisbareRechnung: EingangsrechnungInfo) => (
                      <TableRow
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={(event) => {
                          if (event.ctrlKey || event.metaKey) {
                            window.open(
                              "/eingangsrechnungen/" + zuweisbareRechnung.id,
                              "_blank"
                            );
                          } else {
                            navigate(
                              "/eingangsrechnungen/" + zuweisbareRechnung.id
                            );
                          }
                        }}
                      >
                        <TableCell padding="none">
                          <Box
                            display="flex"
                            alignItems="center"
                            sx={{ paddingRight: "1em" }}
                          >
                            {!rechnungZugewiesen && (
                              <Checkbox
                                checked={
                                  checkedRechnungId === zuweisbareRechnung.id
                                }
                                onChange={() => {
                                  if (belegTyp.toLowerCase() === "storno") {
                                    setAusgewaehlteRechnungId(
                                      zuweisbareRechnung.id
                                    );
                                    setStornoDialogOpen(true);
                                  } else {
                                    handleToggle(zuweisbareRechnung.id);
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                disabled={loading}
                              />
                            )}
                            <RechnungStatusChip
                              eingangsrechnung={zuweisbareRechnung}
                            />
                          </Box>
                        </TableCell>
                        <TableCell padding="none">
                          {formatDate(zuweisbareRechnung.rechnungsDatum)}
                        </TableCell>
                        <TableCell>
                          {zuweisbareRechnung.rechnungsNummer}
                        </TableCell>
                        <TableCell
                          sx={{ paddingLeft: "15px", textAlign: "right" }}
                        >
                          {formatAsMoney(zuweisbareRechnung.bruttoBetrag)}
                        </TableCell>
                        <TableCell
                          sx={{ paddingLeft: "15px", textAlign: "right" }}
                        >
                          {formatAsMoney(zuweisbareRechnung.nettoBetrag)}
                        </TableCell>
                      </TableRow>
                    )
                  )
                ) : (
                  <></>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
      <Dialog
        open={stornoDialogOpen}
        onClose={() => setStornoDialogOpen(false)}
      >
        <DialogTitle id="alert-dialog-title">Achtung</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Beim Stornieren dieser Rechnung werden folgende Änderungen
            vorgenommen:
            <ul>
              <li>
                Der Status der Rechnung wird auf <strong>„Storniert“</strong>{" "}
                gesetzt.
              </li>
              <li>
                Alle <strong>Baustellen-Zuweisungen</strong> werden entfernt.
              </li>
              <li>
                Alle <strong>Materialtransport-Zuweisungen</strong> werden
                gelöscht.
              </li>
              <li>
                Die damit verbundenen <strong>Umsätze</strong> werden gelöscht.
              </li>
              <li>
                Falls die Rechnung <strong>noch nicht bezahlt</strong> wurde,
                wird sie aus dem <strong>Zahllauf</strong> entfernt.
              </li>
              <li>
                Diese Zuweisungsliste kann danach{" "}
                <strong>nicht mehr bearbeitet</strong> werden.
              </li>
            </ul>
            Möchten Sie die Rechnung wirklich stornieren?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStornoDialogOpen(false)} color="inherit">
            Abbrechen
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (ausgewaehlteRechnungId) {
                handleToggle(ausgewaehlteRechnungId);
              }
              setStornoDialogOpen(false);
            }}
          >
            Stornieren
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
